import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { DialogFooter } from '../ui/dialog';
import { Upload, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { SectionEditor } from './SectionEditor';

const COLORS = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#EC4899', '#06B6D4'];

function VideoSlot({ label, accept, currentUrl, file, setFile, helper, testid }) {
  const display = file ? file.name : (currentUrl ? `Replace ${label}…` : `Choose ${label}…`);
  return (
    <div>
      <Label className="text-gray-300 text-xs">{label}</Label>
      <div className="mt-1">
        <label className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded cursor-pointer hover:bg-gray-750 transition text-sm text-gray-300">
          <Upload className="w-4 h-4" />
          {display}
          <input
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => setFile(e.target.files[0] || null)}
            data-testid={testid}
          />
        </label>
        {currentUrl && !file && (
          <p className="text-[10px] text-green-400 mt-1">Current: {currentUrl.split('/').pop()}</p>
        )}
        {helper && <p className="text-[10px] text-gray-500 mt-1">{helper}</p>}
      </div>
    </div>
  );
}

export function VideoForm({ video, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    title: video?.title || '', subtitle: video?.subtitle || '', description: video?.description || '',
    duration_label: video?.duration_label || '3 min', duration_seconds: video?.duration_seconds || 180,
    color: video?.color || '#3B82F6', is_published: video?.is_published ?? true, sections: video?.sections || [],
  });
  const [videoFileWebm, setVideoFileWebm] = useState(null);
  const [videoFileMp4, setVideoFileMp4] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);

  const isEdit = !!video?.video_id;
  const hasExistingWebm = !!video?.video_url_webm;
  const hasExistingMp4 = !!video?.video_url_mp4;
  const hasLegacyVideo = !!video?.video_url && !hasExistingWebm && !hasExistingMp4;

  // After save, will the row have an MP4 fallback available?
  const willHaveMp4AfterSave = !!videoFileMp4 || hasExistingMp4;
  // Will it have any video at all?
  const willHaveAnyVideo = !!videoFileWebm || !!videoFileMp4 || hasExistingWebm || hasExistingMp4 || hasLegacyVideo;

  const showMp4Warning = willHaveAnyVideo && !willHaveMp4AfterSave;

  const getSaveLabel = () => {
    if (saving) return 'Saving…';
    return isEdit ? 'Update Video' : 'Create Video';
  };

  const getThumbLabel = () => {
    if (thumbFile) return thumbFile.name;
    return video?.thumbnail_url ? 'Replace thumbnail…' : 'Choose thumbnail…';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    const formData = new FormData();
    Object.entries({
      title: form.title, subtitle: form.subtitle, description: form.description,
      duration_label: form.duration_label, duration_seconds: form.duration_seconds,
      color: form.color, is_published: form.is_published,
    }).forEach(([k, v]) => formData.append(k, v));
    formData.append('sections_json', JSON.stringify(form.sections));
    if (videoFileWebm) formData.append('video_file_webm', videoFileWebm);
    if (videoFileMp4) formData.append('video_file_mp4', videoFileMp4);
    if (thumbFile) formData.append('thumbnail_file', thumbFile);
    onSave(formData, video?.video_id);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-gray-300 text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="Video title" required data-testid="video-form-title" /></div>
        <div><Label className="text-gray-300 text-xs">Subtitle</Label><Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="Short subtitle" /></div>
      </div>
      <div><Label className="text-gray-300 text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-gray-800 border-gray-700 text-white text-sm" rows={2} placeholder="Brief description" /></div>
      <div className="grid grid-cols-3 gap-3">
        <div><Label className="text-gray-300 text-xs">Duration Label</Label><Input value={form.duration_label} onChange={(e) => setForm({ ...form, duration_label: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="3 min" /></div>
        <div><Label className="text-gray-300 text-xs">Duration (seconds)</Label><Input type="number" value={form.duration_seconds} onChange={(e) => setForm({ ...form, duration_seconds: parseInt(e.target.value) || 0 })} className="bg-gray-800 border-gray-700 text-white" /></div>
        <div>
          <Label className="text-gray-300 text-xs">Color</Label>
          <div className="flex gap-1.5 mt-1">
            {COLORS.map(c => (<button key={c} type="button" onClick={() => setForm({ ...form, color: c })} className={`w-6 h-6 rounded-full border-2 transition ${form.color === c ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'}`} style={{ background: c }} />))}
          </div>
        </div>
      </div>

      {/* Video files: dual source */}
      <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3 space-y-3" data-testid="video-form-files">
        <div className="flex items-center justify-between">
          <Label className="text-gray-300 text-xs uppercase tracking-wide">Video Files</Label>
          <p className="text-[10px] text-gray-500">Upload both for full browser coverage</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <VideoSlot
            label="Primary (WebM)"
            accept=".webm,video/webm"
            currentUrl={video?.video_url_webm}
            file={videoFileWebm}
            setFile={setVideoFileWebm}
            helper="Royalty-free. Plays in Chrome / Firefox / Edge / modern Safari."
            testid="video-file-webm-input"
          />
          <VideoSlot
            label="Fallback (MP4)"
            accept=".mp4,.m4v,video/mp4"
            currentUrl={video?.video_url_mp4}
            file={videoFileMp4}
            setFile={setVideoFileMp4}
            helper="Universal compatibility. Plays on older iPhones / Intel Macs."
            testid="video-file-mp4-input"
          />
        </div>
        {hasLegacyVideo && (
          <p className="text-[10px] text-blue-300" data-testid="video-form-legacy-hint">
            Legacy single-source video on file ({video.video_url.split('/').pop()}). Will keep playing until you upload at least one of the slots above.
          </p>
        )}
        {showMp4Warning && (
          <div className="flex items-start gap-2 p-2 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px]" data-testid="video-form-mp4-warning">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>No MP4 fallback uploaded. Users on older iPhones / Intel Macs may see a black box. You can still save and add MP4 later.</span>
          </div>
        )}
      </div>

      <div>
        <Label className="text-gray-300 text-xs">Thumbnail</Label>
        <div className="mt-1">
          <label className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded cursor-pointer hover:bg-gray-750 transition text-sm text-gray-300">
            <Upload className="w-4 h-4" />
            {getThumbLabel()}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => setThumbFile(e.target.files[0] || null)} data-testid="thumbnail-file-input" />
          </label>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setForm({ ...form, is_published: !form.is_published })} className={`w-10 h-5 rounded-full transition relative ${form.is_published ? 'bg-green-500' : 'bg-gray-600'}`}>
          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${form.is_published ? 'left-5' : 'left-0.5'}`} />
        </button>
        <span className="text-sm text-gray-300">{form.is_published ? 'Published' : 'Draft'}</span>
      </div>
      <SectionEditor sections={form.sections} onChange={(s) => setForm({ ...form, sections: s })} />
      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="video-form-save">{getSaveLabel()}</Button>
      </DialogFooter>
    </form>
  );
}
